/**
 * The daily premium: R30 a day, what is owed, and how to settle it.
 *
 * §2's dashboard. The design decision that shapes the whole screen is the
 * order: **status, amount, action, then explanation**. Somebody opening this
 * has one of two questions — "am I about to be cut off?" and "how do I pay?" —
 * and both are answered above the fold on a phone. The ledger is below, for the
 * third question, which is asked far less often and always after the first two.
 *
 * Every colour is paired with a word. §12's UI row asks for "status colours plus
 * text labels", and it is not only an accessibility box: a red badge on its own
 * says something is wrong and not what, and this screen is read in a hurry on a
 * cracked phone in bad light.
 */

import { useQuery } from "@tanstack/react-query";
import {
  AlertTriangle,
  CalendarClock,
  CheckCircle2,
  Clock,
  Wallet,
} from "lucide-react";
import { useState } from "react";

import {
  Button,
  Card,
  EmptyState,
  ErrorState,
  Pill,
  SkeletonRows,
  StatCard,
  TBody,
  TD,
  TH,
  THead,
  TR,
  Table,
  type PillTone,
} from "@/components/ui";
import { formatDate, formatMoney, plural } from "@/lib/format";
import {
  premiumBillingRepository,
  type LedgerEntry,
  type PremiumOverview,
  type PremiumStatus,
} from "@/repositories/premiumBillingRepository";

import { PayNowDialog } from "./PayNowDialog";

const TONES: Record<PremiumStatus, PillTone> = {
  PAID: "success",
  DUE_TODAY: "warn",
  OVERDUE: "danger",
  PAYMENT_PENDING: "info",
  SUSPENDED: "danger",
  INACTIVE: "neutral",
};

const LABELS: Record<PremiumStatus, string> = {
  PAID: "Paid up",
  DUE_TODAY: "Due today",
  OVERDUE: "Overdue",
  PAYMENT_PENDING: "Payment pending",
  SUSPENDED: "Suspended",
  INACTIVE: "Not billing",
};

export function DailyPremium() {
  const [paying, setPaying] = useState(false);

  const overview = useQuery({
    queryKey: ["premium", "overview"],
    queryFn: () => premiumBillingRepository.overview(),
    // A payment clears through a provider webhook, so the answer can change
    // without this tab doing anything. Refetching on focus is what makes
    // "I paid on my phone, why does the till still say overdue" not happen.
    refetchOnWindowFocus: true,
  });

  if (overview.isError) {
    return (
      <ErrorState
        title="Your account could not load"
        onRetry={() => overview.refetch()}
      />
    );
  }
  if (overview.isLoading || !overview.data) {
    return <SkeletonRows rows={6} />;
  }

  const data = overview.data;

  return (
    <div className="space-y-5">
      <StatusBanner overview={data} onPay={() => setPaying(true)} />

      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
        <StatCard
          label="OUTSTANDING"
          value={formatMoney(data.amount_due)}
          hint={
            data.oldest_unpaid_date
              ? `Oldest unpaid ${formatDate(data.oldest_unpaid_date)}`
              : "Nothing owing"
          }
          icon={Wallet}
          tone={data.amount_due_cents > 0 ? "danger" : "brand"}
        />
        <StatCard
          label="PAID THROUGH"
          value={data.paid_through ? formatDate(data.paid_through) : null}
          hint="Last day fully covered"
          icon={CheckCircle2}
          tone="brand"
        />
        <StatCard
          label="NEXT CHARGE"
          value={
            data.next_charge_date ? formatDate(data.next_charge_date) : null
          }
          hint={`${formatMoney(data.account.daily_rate)} a day`}
          icon={CalendarClock}
          tone="info"
        />
        <StatCard
          label="CREDIT ON ACCOUNT"
          value={formatMoney(data.available_credit)}
          hint="Applied to the coming days"
          icon={Clock}
          tone={data.available_credit_cents > 0 ? "brand" : "info"}
        />
      </div>

      <RecentActivity entries={data.recent_entries} />

      <PayNowDialog
        open={paying}
        onClose={() => setPaying(false)}
        overview={data}
        onPaid={() => {
          setPaying(false);
          void overview.refetch();
        }}
      />
    </div>
  );
}

/**
 * The one thing the merchant came to find out, said in a sentence.
 *
 * Written per status rather than assembled from fragments. "You owe R90.00 and
 * have 2 days to pay it" and "Your service is restricted until R90.00 is paid"
 * are different messages with different urgency, and a template with a
 * conditional clause in the middle produces neither of them well.
 */
function StatusBanner({
  overview,
  onPay,
}: {
  overview: PremiumOverview;
  onPay: () => void;
}) {
  const owed = formatMoney(overview.amount_due);

  const message = (() => {
    switch (overview.status) {
      case "PAID":
        return overview.available_credit_cents > 0
          ? `You are paid up, with ${formatMoney(overview.available_credit)} in credit.`
          : "You are paid up. Nothing is owing.";
      case "DUE_TODAY":
        return `${owed} is due today.`;
      case "OVERDUE":
        return overview.grace_days_remaining > 0
          ? `${owed} is outstanding. You have ${plural(
              overview.grace_days_remaining,
              "day",
            )} to settle it.`
          : `${owed} is outstanding and past the grace period.`;
      case "PAYMENT_PENDING":
        return "A payment is being confirmed. Please do not pay again yet.";
      case "SUSPENDED":
        return `Your service is restricted until ${owed} is paid. It comes back on as soon as the payment clears.`;
      case "INACTIVE":
        return "This account is not being billed.";
    }
  })();

  const urgent =
    overview.status === "OVERDUE" || overview.status === "SUSPENDED";

  return (
    <Card className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
      <div className="flex items-start gap-3">
        {urgent ? (
          <AlertTriangle
            className="mt-0.5 size-5 shrink-0 text-danger-500"
            aria-hidden
          />
        ) : (
          <CheckCircle2
            className="mt-0.5 size-5 shrink-0 text-money-500"
            aria-hidden
          />
        )}
        <div>
          <div className="flex items-center gap-2">
            <Pill tone={TONES[overview.status]}>{LABELS[overview.status]}</Pill>
            <span className="text-xs text-ink-500">
              {overview.merchant_number || overview.merchant_id}
            </span>
          </div>
          <p className="mt-1.5 text-sm text-ink-600">{message}</p>
        </div>
      </div>

      {/* §2's CTA rule: "Pay now" above zero, "Make advance payment" at zero.
          The button never disappears — a merchant who wants to pay ahead should
          not have to wait to fall behind first. */}
      <Button
        variant={overview.can_pay ? "money" : "secondary"}
        onClick={onPay}
        disabled={
          overview.status === "PAYMENT_PENDING" ||
          overview.status === "INACTIVE"
        }
      >
        {overview.can_pay ? `Pay ${owed}` : "Make advance payment"}
      </Button>
    </Card>
  );
}

/** §2: the latest five ledger entries, newest first. */
function RecentActivity({ entries }: { entries: LedgerEntry[] }) {
  return (
    <Card flush className="p-5">
      <h2 className="mb-4 text-sm font-semibold text-ink-900">
        Recent activity
      </h2>

      {entries.length === 0 ? (
        <EmptyState
          title="Nothing yet"
          description="Your daily charges and payments appear here."
        />
      ) : (
        <Table>
          <THead>
            <TH>Date</TH>
            <TH>Description</TH>
            <TH hideBelow="md">Reference</TH>
            <TH className="text-right">Amount</TH>
          </THead>
          <TBody>
            {entries.map((entry) => (
              <TR key={entry.id}>
                <TD className="text-sm text-ink-500">
                  {formatDate(entry.service_date)}
                </TD>
                <TD className="text-sm text-ink-900">
                  {entry.description}
                  {entry.status === "REVERSED" && (
                    <Pill tone="neutral" className="ml-2">
                      Reversed
                    </Pill>
                  )}
                </TD>
                <TD hideBelow="md" className="font-mono text-xs text-ink-500">
                  {entry.external_reference || "—"}
                </TD>
                <TD
                  className={
                    entry.direction === "CREDIT"
                      ? "text-right tabular-nums text-money-700"
                      : "text-right tabular-nums text-ink-900"
                  }
                >
                  {entry.direction === "CREDIT" ? "−" : ""}
                  {formatMoney(entry.amount)}
                </TD>
              </TR>
            ))}
          </TBody>
        </Table>
      )}
    </Card>
  );
}
